import { Alert } from "@/components/ui/Alert";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/Command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";
import {
  PageTabsList,
  PageTabsTrigger,
  Tabs,
  TabsContent,
} from "@/components/ui/Tabs";
import { Text } from "@/components/ui/Text";
import type { ScopeDefinition } from "@gram/client/models/components/scopedefinition.js";
import type { Scope } from "@gram/client/models/components/rolegrant.js";
import { Check, Plus, X } from "lucide-react";
import { useRef, useState, type JSX, type ReactNode } from "react";
import type { ResourceType } from "./types";

export interface ScopeGroup {
  label: string;
  resourceType: ResourceType;
  description: string;
  scopes: ScopeDefinition[];
}

/**
 * MCP permissions for administering servers rather than using them. They sit
 * under Platform access, but each still lets its holder connect to the servers
 * it covers with every tool (scopeExpansions in server/internal/authz/scopes.go).
 */
const MCP_ADMIN_SCOPES: ReadonlySet<string> = new Set([
  "mcp:read",
  "mcp:write",
]);

function isMcpAccessScope(scope: ScopeDefinition): boolean {
  return scope.resourceType === "mcp" && !MCP_ADMIN_SCOPES.has(scope.slug);
}

function mcpTabLabel(
  serverCount: number | null | undefined,
  permissionCount: number,
): string {
  if (serverCount === null) return "MCP access";
  return `MCP access (${serverCount ?? permissionCount})`;
}

/**
 * The permissions a role carries, as a list you build rather than a tree you
 * walk: pick permissions from one searchable menu, then narrow each one on its
 * own row. Connecting to MCP servers is separated from the rest because it is
 * the one with something to narrow — a server, and inside it, particular tools.
 */
export function RolePermissionsSection({
  groups,
  selectedScopes,
  disabled,
  onToggleScope,
  renderScopeRule,
  subjectLabel = "this role",
  markAgentIneligible = false,
  renderMcpAccess,
  mcpAccessCount,
}: {
  groups: ScopeGroup[];
  selectedScopes: Set<string>;
  disabled?: boolean;
  onToggleScope: (scope: Scope) => void;
  /** Right-hand side of a row: the rule chips that narrow this permission. */
  renderScopeRule: (scope: ScopeDefinition) => ReactNode;
  /** What the empty states call the thing being given permissions. */
  subjectLabel?: string;
  /**
   * Mark the permissions agents cannot hold. A role's permissions are shared
   * by everything assigned to it, but an agent's policy is filtered against
   * the agent runtime allowlist every time it loads, so these are dropped for
   * its agent members rather than granted. Set once the role has any.
   */
  markAgentIneligible?: boolean;
  /**
   * Replaces the MCP access tab's permission list with a server picker. Given
   * a way back to Platform access, where the admin permissions that also
   * connect are managed.
   */
  renderMcpAccess?: (controls: { showPlatformAccess: () => void }) => ReactNode;
  /**
   * What the MCP access tab counts when it shows servers, not permissions.
   * Null while that count is still loading.
   */
  mcpAccessCount?: number | null;
}): JSX.Element {
  const [tab, setTab] = useState("mcp");
  const [pickerOpen, setPickerOpen] = useState(false);
  // A pick from the empty state waits here until the picker has closed.
  const pendingScope = useRef<Scope | null>(null);

  // Split by permission, not by group: the MCP group's admin permissions
  // belong with the platform ones.
  const groupsWith = (keep: (scope: ScopeDefinition) => boolean) =>
    groups
      .map((group) => ({ ...group, scopes: group.scopes.filter(keep) }))
      .filter((group) => group.scopes.length > 0);
  const mcpGroups = groupsWith(isMcpAccessScope);
  const otherGroups = groupsWith((scope) => !isMcpAccessScope(scope));

  const selectedIn = (list: ScopeGroup[]) =>
    list.flatMap((group) =>
      group.scopes.filter((scope) => selectedScopes.has(scope.slug)),
    );

  const mcpSelected = selectedIn(mcpGroups);
  const otherSelected = selectedIn(otherGroups);
  const activeGroups = tab === "mcp" ? mcpGroups : otherGroups;
  const activeSelected = tab === "mcp" ? mcpSelected : otherSelected;
  const showsMcpAccess = tab === "mcp" && !!renderMcpAccess;

  const addButton = (
    <PopoverTrigger asChild>
      <Button
        variant="secondary"
        size="sm"
        disabled={disabled}
        // The header bar is tinted, so the button keeps its own surface
        // rather than dissolving into it.
        className="bg-background"
      >
        <Button.LeftIcon>
          <Plus className="h-4 w-4" />
        </Button.LeftIcon>
        <Button.Text>Add permissions</Button.Text>
      </Button>
    </PopoverTrigger>
  );

  return (
    <div className="border-border border">
      {/* gap-0: the tab strip and the list share one bordered box, so the
            Tabs default gap left the first row sitting lower than the rest. */}
      {/* One picker, opened from the empty state while the tab has no
            permissions and from the header once it has some. */}
      <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
        <Tabs value={tab} onValueChange={setTab} className="gap-0">
          <div className="border-border bg-muted/30 flex items-center justify-between gap-3 border-b px-4">
            <PageTabsList>
              <PageTabsTrigger value="mcp">
                {mcpTabLabel(mcpAccessCount, mcpSelected.length)}
              </PageTabsTrigger>
              <PageTabsTrigger value="organization">
                Platform access ({otherSelected.length})
              </PageTabsTrigger>
            </PageTabsList>

            {activeSelected.length > 0 && !showsMcpAccess && addButton}
            <PopoverContent
              align="end"
              className="w-[min(24rem,calc(100vw-2rem))] p-0"
              // Runs once the picker has fully closed.
              onCloseAutoFocus={() => {
                if (pendingScope.current) {
                  onToggleScope(pendingScope.current);
                  pendingScope.current = null;
                }
              }}
            >
              <Command className="[&_[data-slot=command-input-wrapper]]:h-10 [&_[data-slot=command-input]]:h-10">
                <CommandInput placeholder="Search permissions" />
                <CommandList className="max-h-80">
                  <CommandEmpty>No permission matches.</CommandEmpty>
                  {activeGroups.map((group) => (
                    <CommandGroup key={group.label} heading={group.label}>
                      {group.scopes.map((scope) => (
                        <CommandItem
                          key={scope.slug}
                          value={`${scope.slug} ${scope.description}`}
                          onSelect={() => {
                            // The first pick moves the trigger from the empty
                            // state to the header. Close first and add once
                            // the picker is gone, so it never flashes at the
                            // header while it animates out.
                            if (activeSelected.length === 0) {
                              pendingScope.current = scope.slug as Scope;
                              setPickerOpen(false);
                              return;
                            }
                            onToggleScope(scope.slug as Scope);
                          }}
                          className="items-start gap-2"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="font-mono text-sm">
                              {scope.slug}
                            </div>
                            <div className="text-muted-foreground text-xs">
                              {scope.description}
                            </div>
                          </div>
                          {/* A tick marks what the role already has; a
                                checkbox implied a second, separate state. */}
                          {selectedScopes.has(scope.slug) && (
                            <>
                              {/* cmdk owns aria-selected on its items, so
                                    ownership is spoken as text instead. */}
                              <span className="sr-only">Already added</span>
                              <Check className="mt-0.5 h-4 w-4 shrink-0" />
                            </>
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  ))}
                </CommandList>
              </Command>
            </PopoverContent>
          </div>

          {/* One height whether empty, one row, or several: the box is
              directly under a popover trigger, so a box that grew or shrank as
              permissions came and went would shift the page under the cursor. */}
          <TabsContent value={tab} forceMount className="min-h-[13.5rem]">
            {showsMcpAccess ? (
              renderMcpAccess?.({
                showPlatformAccess: () => setTab("organization"),
              })
            ) : activeSelected.length === 0 ? (
              <div className="flex min-h-[13.5rem] flex-col items-center justify-center px-4 text-center">
                <Text variant="body" className="font-medium">
                  {tab === "mcp"
                    ? "No MCP permissions"
                    : "No platform permissions"}
                </Text>
                <Text muted small className="mt-1">
                  {tab === "mcp"
                    ? `Add a permission to let ${subjectLabel} reach MCP servers and their tools.`
                    : `Add a permission to let ${subjectLabel} work with projects, environments, skills and MCP servers.`}
                </Text>
                <div className="mt-4">{addButton}</div>
              </div>
            ) : (
              <div className="divide-border divide-y">
                {activeSelected.map((scope) => {
                  const rule = renderScopeRule(scope);
                  return (
                    <div
                      key={scope.slug}
                      className="flex items-start gap-3 px-4 py-3"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <Text
                            variant="body"
                            className="font-mono text-sm font-medium"
                          >
                            {scope.slug}
                          </Text>
                          {markAgentIneligible &&
                            scope.agentEligible === false && (
                              <Badge variant="neutral" size="sm">
                                Not available to agents
                              </Badge>
                            )}
                        </div>
                        <Text muted small>
                          {scope.description}
                        </Text>
                        {/* Only permissions that can be narrowed carry a control;
                          an empty container left the row taller than its
                          neighbours. */}
                        {rule && (
                          <div className="mt-2 flex flex-wrap items-center gap-1.5">
                            {rule}
                          </div>
                        )}
                        {MCP_ADMIN_SCOPES.has(scope.slug) && (
                          <Alert
                            iconName="lock"
                            alignTop
                            className="mt-2 text-xs"
                          >
                            Also allows connecting to these servers with every
                            tool.
                            {renderMcpAccess &&
                              " They show as locked on the MCP access tab."}
                          </Alert>
                        )}
                      </div>
                      <Button
                        variant="tertiary"
                        size="sm"
                        disabled={disabled}
                        onClick={() => onToggleScope(scope.slug as Scope)}
                        aria-label={`Remove ${scope.slug}`}
                      >
                        <Button.LeftIcon>
                          <X className="h-4 w-4" />
                        </Button.LeftIcon>
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </Popover>
    </div>
  );
}
